import { supabase } from '../supabase'
import type {
  RfidColorIdentifier,
  RfidEpcScheme,
  RfidEpcSegment,
  RfidItemIdentifier,
  RfidProductIdentity,
  RfidSizeIdentifier,
  UpdateSchemeInput,
  UpsertColorIdentifierInput,
  UpsertItemIdentifierInput,
  UpsertSizeIdentifierInput,
} from '../../types/rfid'
import {
  DEFAULT_RFID_CONSTANT_CODE,
  DEFAULT_RFID_SERIAL_LENGTH,
  DEFAULT_RFID_TOTAL_LENGTH,
  DEFAULT_RFID_VERSION_CODE,
  getDefaultRfidSegments,
  getSegmentBySource,
  validateIdentifierCode,
  validateScheme,
} from '../../utils/epcComposer'
import type { RfidSegmentSource } from '../../types/rfid'

function mapUniqueViolation(error: { code?: string; message?: string; details?: string }, entityLabel: string): Error {
  if (error.code === '23505') {
    const details = error.details || error.message || ''
    if (details.includes('item_code') || details.includes('code'))
      return new Error(`That ${entityLabel} identifier is already used by another entry`)
    if (details.includes('item_name'))
      return new Error('An identifier already exists for this item name')
    if (details.includes('style_code'))
      return new Error('That style code is already used by another item')
    if (details.includes('color_id'))
      return new Error('This color already has an identifier')
    if (details.includes('size_id'))
      return new Error('This size already has an identifier')
    return new Error(`That ${entityLabel} identifier conflicts with an existing value`)
  }
  return new Error(error.message || `Failed to save ${entityLabel} identifier`)
}

function parseSegments(raw: unknown): RfidEpcSegment[] {
  if (!Array.isArray(raw)) return getDefaultRfidSegments()
  return raw.map((segment) => ({
    key: String(segment?.key ?? ''),
    label: String(segment?.label ?? ''),
    length: Number(segment?.length) || 0,
    source: segment?.source,
  })) as RfidEpcSegment[]
}

function mapScheme(row: Record<string, unknown>): RfidEpcScheme {
  return {
    id: String(row.id),
    admin_id: String(row.admin_id),
    name: String(row.name ?? 'Default EPC scheme'),
    is_active: Boolean(row.is_active),
    version_code: String(row.version_code ?? DEFAULT_RFID_VERSION_CODE),
    constant_code: String(row.constant_code ?? ''),
    serial_length: Number(row.serial_length) || DEFAULT_RFID_SERIAL_LENGTH,
    total_length: Number(row.total_length) || DEFAULT_RFID_TOTAL_LENGTH,
    segments: parseSegments(row.segments),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  }
}

export async function getOrCreateActiveScheme(adminId: string): Promise<RfidEpcScheme> {
  if (!adminId) throw new Error('Admin ID is required')

  const { data: existing, error: fetchError } = await supabase
    .from('rfid_epc_schemes')
    .select('*')
    .eq('admin_id', adminId)
    .eq('is_active', true)
    .maybeSingle()

  if (fetchError) throw new Error(fetchError.message || 'Failed to load RFID EPC scheme')
  if (existing) return mapScheme(existing)

  const segments = getDefaultRfidSegments()
  const validation = validateScheme({
    segments,
    serialLength: DEFAULT_RFID_SERIAL_LENGTH,
    totalLength: DEFAULT_RFID_TOTAL_LENGTH,
    versionCode: DEFAULT_RFID_VERSION_CODE,
    constantCode: DEFAULT_RFID_CONSTANT_CODE,
  })
  if (!validation.isValid)
    throw new Error(validation.errors[0] || 'Default EPC scheme is invalid')

  const { data: inserted, error: insertError } = await supabase
    .from('rfid_epc_schemes')
    .insert({
      admin_id: adminId,
      name: 'Default EPC scheme',
      is_active: true,
      version_code: DEFAULT_RFID_VERSION_CODE,
      constant_code: DEFAULT_RFID_CONSTANT_CODE,
      serial_length: DEFAULT_RFID_SERIAL_LENGTH,
      total_length: DEFAULT_RFID_TOTAL_LENGTH,
      segments,
    })
    .select('*')
    .single()

  if (insertError) {
    if (insertError.code === '23505') {
      const { data: raced } = await supabase
        .from('rfid_epc_schemes')
        .select('*')
        .eq('admin_id', adminId)
        .eq('is_active', true)
        .maybeSingle()
      if (raced) return mapScheme(raced)
    }
    throw new Error(insertError.message || 'Failed to create default RFID EPC scheme')
  }

  return mapScheme(inserted)
}

export async function updateScheme(
  adminId: string,
  schemeId: string,
  input: UpdateSchemeInput
): Promise<RfidEpcScheme> {
  if (!adminId) throw new Error('Admin ID is required')
  if (!schemeId) throw new Error('Scheme ID is required')

  const current = await getOrCreateActiveScheme(adminId)
  if (current.id !== schemeId)
    throw new Error('Only the active EPC scheme can be updated')

  const nextSegments = input.segments ?? current.segments
  const nextSerial = input.serial_length ?? current.serial_length
  const nextTotal = input.total_length ?? current.total_length
  const nextVersion = input.version_code ?? current.version_code
  const nextConstant = input.constant_code ?? current.constant_code

  const validation = validateScheme({
    segments: nextSegments,
    serialLength: nextSerial,
    totalLength: nextTotal,
    versionCode: nextVersion,
    constantCode: nextConstant,
  })
  if (!validation.isValid)
    throw new Error(validation.errors[0] || 'Invalid EPC scheme')

  const { data, error } = await supabase
    .from('rfid_epc_schemes')
    .update({
      name: input.name ?? current.name,
      version_code: nextVersion,
      constant_code: nextConstant,
      serial_length: nextSerial,
      total_length: nextTotal,
      segments: nextSegments,
      updated_at: new Date().toISOString(),
    })
    .eq('id', schemeId)
    .eq('admin_id', adminId)
    .select('*')
    .single()

  if (error) throw new Error(error.message || 'Failed to update RFID EPC scheme')
  return mapScheme(data)
}

export async function listItemIdentifiers(adminId: string): Promise<RfidItemIdentifier[]> {
  const { data, error } = await supabase
    .from('rfid_item_identifiers')
    .select('*')
    .eq('admin_id', adminId)
    .order('item_name')

  if (error) throw new Error(error.message || 'Failed to load item identifiers')
  return (data || []) as RfidItemIdentifier[]
}

export async function listColorIdentifiers(adminId: string): Promise<RfidColorIdentifier[]> {
  const { data, error } = await supabase
    .from('rfid_color_identifiers')
    .select('*')
    .eq('admin_id', adminId)

  if (error) throw new Error(error.message || 'Failed to load color identifiers')
  return (data || []) as RfidColorIdentifier[]
}

export async function listSizeIdentifiers(adminId: string): Promise<RfidSizeIdentifier[]> {
  const { data, error } = await supabase
    .from('rfid_size_identifiers')
    .select('*')
    .eq('admin_id', adminId)

  if (error) throw new Error(error.message || 'Failed to load size identifiers')
  return (data || []) as RfidSizeIdentifier[]
}

export async function listProductIdentities(adminId: string): Promise<RfidProductIdentity[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, sku, barcode, category, color_id, size_id')
    .eq('admin_id', adminId)
    .order('name')

  if (error) throw new Error(error.message || 'Failed to load products for RFID matrix')
  return (data || []) as RfidProductIdentity[]
}

async function requireValidCode(params: {
  adminId: string
  source: RfidSegmentSource
  value: string
  label: string
}): Promise<void> {
  const scheme = await getOrCreateActiveScheme(params.adminId)
  const segment = getSegmentBySource(scheme, params.source)
  if (!segment) return

  const error = validateIdentifierCode({
    value: params.value,
    length: segment.length,
    label: params.label,
  })
  if (error) throw new Error(error)
}

export async function upsertItemIdentifier(
  adminId: string,
  input: UpsertItemIdentifierInput
): Promise<RfidItemIdentifier> {
  if (!adminId) throw new Error('Admin ID is required')
  const itemName = input.item_name?.trim()
  const itemCode = input.item_code?.trim()
  if (!itemName) throw new Error('Item name is required')
  if (!itemCode) throw new Error('Item identifier is required')

  const styleCode = input.style_code?.trim() || ''
  const epcYear = input.epc_year?.trim() || ''
  const epcSeason = input.epc_season?.trim() || ''

  if (styleCode)
    await requireValidCode({ adminId, source: 'item_style', value: styleCode, label: 'Style code' })
  if (epcYear)
    await requireValidCode({ adminId, source: 'item_year', value: epcYear, label: 'EPC year' })
  if (epcSeason)
    await requireValidCode({ adminId, source: 'item_season', value: epcSeason, label: 'EPC season' })

  const payload = {
    admin_id: adminId,
    item_name: itemName,
    item_code: itemCode,
    style_code: input.style_code?.trim() || null,
    epc_year: input.epc_year?.trim() || null,
    epc_season: input.epc_season?.trim() || null,
    updated_at: new Date().toISOString(),
  }

  const { data, error } = await supabase
    .from('rfid_item_identifiers')
    .upsert(payload, { onConflict: 'admin_id,item_name' })
    .select('*')
    .single()

  if (error) throw mapUniqueViolation(error, 'item')
  return data as RfidItemIdentifier
}

export async function upsertColorIdentifier(
  adminId: string,
  input: UpsertColorIdentifierInput
): Promise<RfidColorIdentifier> {
  if (!adminId) throw new Error('Admin ID is required')
  if (!input.color_id) throw new Error('Color is required')
  const code = input.code?.trim()
  if (!code) throw new Error('Color identifier is required')
  await requireValidCode({ adminId, source: 'color_code', value: code, label: 'Color code' })

  const { data, error } = await supabase
    .from('rfid_color_identifiers')
    .upsert(
      {
        admin_id: adminId,
        color_id: input.color_id,
        code,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'admin_id,color_id' }
    )
    .select('*')
    .single()

  if (error) throw mapUniqueViolation(error, 'color')
  return data as RfidColorIdentifier
}

export async function upsertSizeIdentifier(
  adminId: string,
  input: UpsertSizeIdentifierInput
): Promise<RfidSizeIdentifier> {
  if (!adminId) throw new Error('Admin ID is required')
  if (!input.size_id) throw new Error('Size is required')
  const code = input.code?.trim()
  if (!code) throw new Error('Size identifier is required')
  await requireValidCode({ adminId, source: 'size_code', value: code, label: 'Size code' })

  const { data, error } = await supabase
    .from('rfid_size_identifiers')
    .upsert(
      {
        admin_id: adminId,
        size_id: input.size_id,
        code,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'admin_id,size_id' }
    )
    .select('*')
    .single()

  if (error) throw mapUniqueViolation(error, 'size')
  return data as RfidSizeIdentifier
}

export async function getRfidConfiguration(adminId: string): Promise<{
  scheme: RfidEpcScheme
  itemIdentifiers: RfidItemIdentifier[]
  colorIdentifiers: RfidColorIdentifier[]
  sizeIdentifiers: RfidSizeIdentifier[]
  products: RfidProductIdentity[]
}> {
  if (!adminId) throw new Error('Admin ID is required')

  const [scheme, itemIdentifiers, colorIdentifiers, sizeIdentifiers, products] = await Promise.all([
    getOrCreateActiveScheme(adminId),
    listItemIdentifiers(adminId),
    listColorIdentifiers(adminId),
    listSizeIdentifiers(adminId),
    listProductIdentities(adminId),
  ])

  return {
    scheme,
    itemIdentifiers,
    colorIdentifiers,
    sizeIdentifiers,
    products,
  }
}

export interface FillMissingRfidSummary {
  itemsUpdated: number
  colorsCreated: number
  sizesCreated: number
  stylesFilled: number
  yearsFilled: number
  seasonsFilled: number
}

function segmentLength(scheme: RfidEpcScheme, source: string, fallback: number): number {
  const segment = scheme.segments.find((s) => s.source === source)
  return segment?.length || fallback
}

function padCode(value: string | number, length: number): string {
  const raw = String(value).trim()
  if (raw.length >= length) return raw.slice(-length)
  return raw.padStart(length, '0')
}

function isAllDigits(value: string): boolean {
  return /^\d+$/.test(value)
}

function nextSequentialCode(taken: Set<string>, length: number, startAt = 1): string {
  let n = startAt
  const max = Math.pow(10, length) - 1
  while (n <= max) {
    const code = padCode(n, length)
    if (!taken.has(code)) {
      taken.add(code)
      return code
    }
    n += 1
  }
  throw new Error(`No free ${length}-digit codes left in the EPC scheme`)
}

function highestNumericCode(codes: string[]): number {
  let max = 0
  for (const code of codes) {
    if (!isAllDigits(code)) continue
    const n = Number(code)
    if (Number.isFinite(n) && n > max) max = n
  }
  return max
}

export async function fillMissingRfidIdentifiers(adminId: string): Promise<FillMissingRfidSummary> {
  if (!adminId) throw new Error('Admin ID is required')

  const scheme = await getOrCreateActiveScheme(adminId)
  const itemLen = segmentLength(scheme, 'item_code', 2)
  const styleLen = segmentLength(scheme, 'item_style', 6)
  const yearLen = segmentLength(scheme, 'item_year', 2)
  const seasonLen = segmentLength(scheme, 'item_season', 1)
  const colorLen = segmentLength(scheme, 'color_code', 3)
  const sizeLen = segmentLength(scheme, 'size_code', 3)

  const defaultYear = padCode('26', yearLen)
  const defaultSeason = padCode('2', seasonLen)

  const [itemIdentifiers, colorIdentifiers, sizeIdentifiers, products, colorsRes, sizesRes] =
    await Promise.all([
      listItemIdentifiers(adminId),
      listColorIdentifiers(adminId),
      listSizeIdentifiers(adminId),
      listProductIdentities(adminId),
      supabase.from('product_colors').select('id, name').eq('admin_id', adminId).order('name'),
      supabase.from('product_sizes').select('id, name, sort_order').eq('admin_id', adminId).order('sort_order'),
    ])

  if (colorsRes.error) throw new Error(colorsRes.error.message || 'Failed to load colors')
  if (sizesRes.error) throw new Error(sizesRes.error.message || 'Failed to load sizes')

  const colors = (colorsRes.data || []) as Array<{ id: string; name: string }>
  const sizes = (sizesRes.data || []) as Array<{ id: string; name: string; sort_order: number }>

  const summary: FillMissingRfidSummary = {
    itemsUpdated: 0,
    colorsCreated: 0,
    sizesCreated: 0,
    stylesFilled: 0,
    yearsFilled: 0,
    seasonsFilled: 0,
  }

  // --- Colors ---
  const colorById = new Map(colorIdentifiers.map((row) => [row.color_id, row]))
  const takenColorCodes = new Set(colorIdentifiers.map((row) => row.code.trim()))
  let colorCursor = 1

  for (const color of colors) {
    const existing = colorById.get(color.id)
    const existingIsEncodable = !!existing && isAllDigits(existing.code.trim())
    if (existing && existingIsEncodable && existing.code.trim().length === colorLen) continue

    let code: string
    if (existing && !existingIsEncodable) {
      // Not hexadecimal-safe, so it can never reach an EPC — reissue it.
      takenColorCodes.delete(existing.code.trim())
      code = nextSequentialCode(takenColorCodes, colorLen, colorCursor)
    } else if (existing && isAllDigits(existing.code) && existing.code.trim().length < colorLen) {
      const padded = padCode(existing.code.trim(), colorLen)
      code = takenColorCodes.has(padded) && padded !== existing.code.trim()
        ? nextSequentialCode(takenColorCodes, colorLen, colorCursor)
        : padded
      takenColorCodes.add(code)
    } else if (!existing) {
      code = nextSequentialCode(takenColorCodes, colorLen, colorCursor)
    } else {
      continue
    }

    colorCursor = Math.max(colorCursor, Number(code) + 1)
    await upsertColorIdentifier(adminId, { color_id: color.id, code })
    summary.colorsCreated += 1
  }

  // --- Sizes ---
  const sizeById = new Map(sizeIdentifiers.map((row) => [row.size_id, row]))
  const takenSizeCodes = new Set(sizeIdentifiers.map((row) => row.code.trim()))
  let sizeCursor = 1

  for (const size of sizes) {
    const existing = sizeById.get(size.id)
    const existingIsEncodable = !!existing && isAllDigits(existing.code.trim())
    if (existing && existingIsEncodable && existing.code.trim().length === sizeLen) continue

    let code: string
    const name = size.name.trim()
    if (existing && !existingIsEncodable) {
      takenSizeCodes.delete(existing.code.trim())
      code = nextSequentialCode(takenSizeCodes, sizeLen, sizeCursor)
    } else if (!existing && isAllDigits(name) && name.length === sizeLen && !takenSizeCodes.has(name)) {
      code = name
      takenSizeCodes.add(code)
    } else if (existing && isAllDigits(existing.code) && existing.code.trim().length < sizeLen) {
      const padded = padCode(existing.code.trim(), sizeLen)
      code = takenSizeCodes.has(padded) && padded !== existing.code.trim()
        ? nextSequentialCode(takenSizeCodes, sizeLen, sizeCursor)
        : padded
      takenSizeCodes.add(code)
    } else if (!existing) {
      code = nextSequentialCode(takenSizeCodes, sizeLen, sizeCursor)
    } else {
      continue
    }

    sizeCursor = Math.max(sizeCursor, Number(code) || sizeCursor)
    await upsertSizeIdentifier(adminId, { size_id: size.id, code })
    summary.sizesCreated += 1
  }

  // --- Items / style / year / season ---
  const itemByName = new Map(itemIdentifiers.map((row) => [row.item_name, row]))
  const takenItemCodes = new Set(itemIdentifiers.map((row) => row.item_code.trim()))
  const takenStyleCodes = new Set(
    itemIdentifiers
      .map((row) => row.style_code?.trim())
      .filter((code): code is string => !!code)
  )

  const productNames = Array.from(
    new Set(products.map((p) => p.name || 'Unnamed Product'))
  ).sort((a, b) => a.localeCompare(b))

  let itemCursor = highestNumericCode(Array.from(takenItemCodes)) + 1
  let styleCursor = highestNumericCode(Array.from(takenStyleCodes)) + 1

  for (const itemName of productNames) {
    const existing = itemByName.get(itemName)
    let itemCode = existing?.item_code?.trim() || ''
    let styleCode = existing?.style_code?.trim() || ''
    let epcYear = existing?.epc_year?.trim() || ''
    let epcSeason = existing?.epc_season?.trim() || ''
    let changed = false

    if (itemCode && itemCode.length === itemLen) {
      // keep valid
    } else if (itemCode && isAllDigits(itemCode) && itemCode.length < itemLen) {
      const padded = padCode(itemCode, itemLen)
      if (!takenItemCodes.has(padded) || padded === itemCode) {
        takenItemCodes.delete(itemCode)
        takenItemCodes.add(padded)
        itemCode = padded
        changed = true
      } else {
        const next = nextSequentialCode(takenItemCodes, itemLen, itemCursor)
        takenItemCodes.delete(itemCode)
        itemCode = next
        itemCursor = Math.max(itemCursor, Number(next) + 1)
        changed = true
      }
    } else if (!itemCode) {
      itemCode = nextSequentialCode(takenItemCodes, itemLen, itemCursor)
      itemCursor = Math.max(itemCursor, Number(itemCode) + 1)
      changed = true
    }

    if (!styleCode) {
      styleCode = nextSequentialCode(takenStyleCodes, styleLen, styleCursor)
      styleCursor = Math.max(styleCursor, Number(styleCode) + 1)
      summary.stylesFilled += 1
      changed = true
    } else if (!isAllDigits(styleCode)) {
      // A style like 'P271' has no hexadecimal representation, so it can never
      // reach an EPC. Reissue it and leave the display name on the product.
      takenStyleCodes.delete(styleCode)
      styleCode = nextSequentialCode(takenStyleCodes, styleLen, styleCursor)
      styleCursor = Math.max(styleCursor, Number(styleCode) + 1)
      summary.stylesFilled += 1
      changed = true
    } else if (styleCode.length < styleLen) {
      const padded = padCode(styleCode, styleLen)
      if (!takenStyleCodes.has(padded) || padded === styleCode) {
        takenStyleCodes.delete(styleCode)
        takenStyleCodes.add(padded)
        styleCode = padded
      } else {
        takenStyleCodes.delete(styleCode)
        styleCode = nextSequentialCode(takenStyleCodes, styleLen, styleCursor)
        styleCursor = Math.max(styleCursor, Number(styleCode) + 1)
      }
      changed = true
    }

    if (!epcYear || !isAllDigits(epcYear)) {
      epcYear = defaultYear
      summary.yearsFilled += 1
      changed = true
    } else if (epcYear.length !== yearLen) {
      epcYear = padCode(epcYear, yearLen)
      changed = true
    }

    if (!epcSeason || !isAllDigits(epcSeason)) {
      epcSeason = defaultSeason
      summary.seasonsFilled += 1
      changed = true
    } else if (epcSeason.length !== seasonLen) {
      epcSeason = padCode(epcSeason, seasonLen)
      changed = true
    }

    if (!changed) continue

    await upsertItemIdentifier(adminId, {
      item_name: itemName,
      item_code: itemCode,
      style_code: styleCode || null,
      epc_year: epcYear || null,
      epc_season: epcSeason || null,
    })
    summary.itemsUpdated += 1
  }

  return summary
}
